import { apiClient } from './apiClient';
import type { AcademySubscriptionsResponse, AcademyStatsResponse, SubscriptionFilters } from '@/types/academy.types';

/**
 * Academy Service
 * Handles academy subscription operations for super admins
 */

// Request types
export interface AcademyPaymentRequest {
  email: string;
  name: string;
  phone: string;
  months: 3 | 6 | 12;
}

export interface UpgradeCalculationRequest {
  monthsPaidPremium: 3 | 6 | 12;
}

export interface UpgradePaymentRequest {
  email: string;
  monthsPaidPremium: 3 | 6 | 12;
}

// Response data types (inner data structure)
export interface AcademyPaymentData {
  authorizationUrl: string;
  reference: string;
  amount: number;
  metadata: {
    subscriptionType: 'academy';
    months: number;
    name: string;
    phone: string;
  };
}

export interface UpgradeCalculationData {
  premiumMonthlyRate: number;
  academyMonthlyRate: number;
  monthsPaid: number;
  premiumPaid: number;
  academyRequired: number;
  balanceDue: number;
}

export interface UpgradePaymentData {
  authorizationUrl: string;
  reference: string;
  amount: number;
  metadata: {
    subscriptionType: 'academy_upgrade';
    monthsPaid: number;
    balanceDue: number;
    customAmount: number;
    premiumPaid: number;
    academyRequired: number;
  };
}

export const academyService = {
  /**
   * Get academy subscriptions list with filters
   * GET /subscriptions/admin/academy/subscriptions
   */
  getSubscriptions: async (filters?: SubscriptionFilters) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    const url = queryString 
      ? `/subscriptions/admin/academy/subscriptions?${queryString}`
      : '/subscriptions/admin/academy/subscriptions';

    return await apiClient.get<AcademySubscriptionsResponse>(url);
  },

  /**
   * Get academy statistics
   * GET /subscriptions/admin/academy/stats
   */
  getStats: async () => {
    return await apiClient.get<AcademyStatsResponse>('/subscriptions/admin/academy/stats');
  },

  /**
   * Generate academy payment link for new subscription
   * POST /subscriptions/admin/academy/generate-payment-link
   */
  generatePaymentLink: async (data: AcademyPaymentRequest) => {
    return await apiClient.post<AcademyPaymentData>(
      '/subscriptions/admin/academy/generate-payment-link',
      data
    );
  },

  /**
   * Calculate upgrade balance from premium to academy
   * POST /subscriptions/admin/academy/calculate-upgrade
   */
  calculateUpgrade: async (data: UpgradeCalculationRequest) => {
    return await apiClient.post<UpgradeCalculationData>(
      '/subscriptions/admin/academy/calculate-upgrade',
      data
    );
  },

  /**
   * Generate upgrade payment link for balance only
   * POST /subscriptions/admin/academy/generate-upgrade-link
   */
  generateUpgradeLink: async (data: UpgradePaymentRequest) => {
    return await apiClient.post<UpgradePaymentData>(
      '/subscriptions/admin/academy/generate-upgrade-link',
      data
    );
  },
};
